/* @flow */

//----------------------------------------------------------------------//
//                         Imported NPM modules                         //
//----------------------------------------------------------------------//

import * as React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { Link } from 'react-router-dom';

//----------------------------------------------------------------------//
//                     Imported app modules/assets                      //
//----------------------------------------------------------------------//

import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';

import Loading from 'components/Loading/Loading';
import { allRoles } from 'permissions/general';
import type { WidgetItem } from 'stores/DashboardStore';
import type { CommonWidget } from '../../commonTypes';

import ChartLegend from '../ChartLegend/ChartLegend';
import InsufficientData from '../InsufficientData/InsufficientData';
import WidgetCard from '../WidgetCard';
import type { WidgetInfo } from '../WidgetCard';

import styles from './NivoBarChart.module.scss';

//----------------------------------------------------------------------//
//                       Exported React component                       //
//----------------------------------------------------------------------//

export type NivoBarChartProps = {
  layout?: string,
  widgetInfos: WidgetInfo[],
  onVariantSelect?: (variant: string) => void,
  tableTitles?: Array<string>,
  data: any[],
  isLoading: boolean,
  legendKeys?: Array<Object>,
  thumbnail?: React.Node,
  button?: boolean,
  buttonLink?: string | Object,
  buttonText?: string,
  isIE: boolean,
  footer?: React.Node,
} & CommonWidget;

export default class NivoBarChart extends React.PureComponent<NivoBarChartProps> {
  render() {
    const {
      layout,
      widgetInfos,
      onVariantSelect,
      tableTitles,
      data,
      size,
      widgetItem,
      isFullScreen,
      isLoading,
      legendKeys,
      thumbnail,
      button,
      buttonLink,
      buttonText,
      isIE,
      footer = null,
      ...other
    } = this.props;

    const isInteractive = size !== 'thumbnail';

    const margins =
      size === 'thumbnail'
        ? { top: 0, right: 0, bottom: 0, left: 0 }
        : layout === 'vertical'
        ? { top: 24, right: 30, bottom: 90, left: 72 }
        : { top: 40, right: 30, bottom: 48, left: 120 };

    const noOfAssets = data ? data.length : 0;

    const hasGridX = isInteractive && layout !== 'vertical';
    const hasGridY = isInteractive && layout === 'vertical';

    return (
      <WidgetCard
        dataCount={noOfAssets}
        isIE={isIE}
        loading={isLoading}
        permissions={allRoles}
        size={size}
        widgetInfos={widgetInfos}
        widgetItem={widgetItem}
        isFullScreen={isFullScreen}
        onVariantSelect={onVariantSelect}
        legendKeys={size === 'canvas-50' ? legendKeys : undefined}
        button={
          button && buttonLink && buttonText ? { link: buttonLink, label: buttonText } : undefined
        }
        thumbnail={thumbnail}
        contentStyle={size !== 'thumbnail' ? { padding: '44px 0 0' } : undefined}
        footer={footer}
      >
        <div className={`${styles['full-container']} ${styles[size]}`}>
          {isInteractive && tableTitles && (
            <div className={styles['table-headers']}>
              <span>{tableTitles[0]}</span>
              <span>{tableTitles[1]}</span>
            </div>
          )}
          {size === 'canvas' && Boolean(legendKeys) && (
            <ChartLegend
              position="right"
              keys={legendKeys}
              style={{
                position: 'absolute',
                top: '-30px',
                right: '20px',
              }}
            />
          )}
          <div className={styles['bar-container']}>
            <div className={styles['bar-overflow']}>
              <div
                style={{
                  height: '100%',
                  width: '100%',
                  overflow: 'hidden',
                }}
              >
                <ResponsiveBar
                  data={data}
                  layout={layout || 'horizontal'}
                  margin={margins}
                  enableLabel={false}
                  enableGridX={hasGridX}
                  enableGridY={hasGridY}
                  animate={false}
                  motionStiffness={90}
                  motionDamping={15}
                  isInteractive={isInteractive}
                  axisBottom={{
                    renderTick: tick => {
                      return (
                        <g key={tick.key} transform={`translate(${tick.x},${tick.y})`}>
                          <text
                            textAnchor="middle"
                            alignmentBaseline="central"
                            transform="translate(0, 24)"
                          >
                            {Number.isInteger(tick.key) ? tick.key : ''}
                          </text>
                        </g>
                      );
                    },
                  }}
                  axisTop={{
                    renderTick: tick => {
                      return (
                        <g key={tick.key} transform={`translate(${tick.x},${tick.y})`}>
                          <text
                            textAnchor="middle"
                            alignmentBaseline="central"
                            transform="translate(0, -24)"
                          >
                            {Number.isInteger(tick.key) ? tick.key : ''}
                          </text>
                        </g>
                      );
                    },
                  }}
                  {...other}
                />
              </div>
            </div>
          </div>
        </div>
      </WidgetCard>
    );
  }
}
